import { isDesktop } from 'Utils/viewport';
import { requireElement } from './dom';
import type { Host } from './types';

/**
 * Every page-specific selector and all layout maths lives in a host. The client
 * itself never touches the video page, the sidebar, or the iOS bridge.
 */

const sizeFrameToSidebar = (popupFrame: HTMLElement): void => {
    if (!isDesktop()) {
        return;
    }

    const relatedContentOffsetTop = requireElement<HTMLElement>(document, '.related-sidebar').offsetTop;
    popupFrame.style.height = `${window.innerHeight - relatedContentOffsetTop}px`;
};

/**
 * Strip everything a display wrote onto the shared shell.
 *
 * The same markup is reused by whichever client owns the page next, and both the
 * host and the popup write to it — classes on the container, inline styles on
 * the frame, container and popup. Identical on both platforms, so it lives here
 * rather than being duplicated into each host.
 */
const resetShell = (popupFrame: HTMLElement): void => {
    popupFrame.classList.add('is-hidden');
    popupFrame.removeAttribute('style');

    const container = popupFrame.querySelector('.js_popupContainer');
    container?.classList.remove('is-closed', 'is-half-expanded', 'is-full-expanded', 'transitions-disabled');
    container?.removeAttribute('style');

    // Every popup under the frame, not just the first: a rotation can end with an
    // outgoing popup still carrying the crossfade class (`opacity: 0`), and a
    // replacement client would clone that invisible shell as its pristine copy.
    popupFrame.querySelectorAll('.js_brandAlertPopup').forEach((popup) => {
        popup.classList.remove('fade-hidden');
        popup.removeAttribute('style');
    });

    // Tray state is toggled by the user and never re-set by `_populateData`, so a
    // reused shell would open with the ISI or Indications tray already expanded.
    popupFrame.querySelectorAll('.js_secondaryWrap').forEach((wrap) => {
        wrap.classList.remove('safety-expanded', 'indications-expanded');
        wrap.removeAttribute('style');
    });
};

/**
 * The Video Assistant surface.
 *
 * Shares the video page's fixed-overlay geometry — the sideguide is a fixed frame,
 * not a child of the assistant — and differs only in stacking above the
 * assistant's mobile panel, which the `--bva` modifier handles in SCSS.
 */
export class VideoAssistantHost implements Host {
    constructor(private readonly _popupFrame: HTMLElement) {}

    reveal(): void {
        this._popupFrame.classList.remove('is-hidden');
    }

    setPopupHeight(): void {
        sizeFrameToSidebar(this._popupFrame);
    }

    expand(popupContainer: HTMLElement): void {
        const video = requireElement<HTMLElement>(document, '.js_videoTab video');
        const { height: videoHeight } = video.getBoundingClientRect();
        const portraitOrientation = window.innerWidth < window.innerHeight;

        if (portraitOrientation && !isDesktop()) {
            popupContainer.style.height = `calc(100svh - ${videoHeight}px)`;
        }
    }

    close(popup: HTMLElement, popupContainer: HTMLElement): void {
        popupContainer.classList.add('is-closed');
        popup.removeAttribute('style');
    }

    reset(): void {
        resetShell(this._popupFrame);
    }

    destroy(): void {}
}

/**
 * The video player page
 */
export class VideoPageHost implements Host {
    constructor(private readonly _popupFrame: HTMLElement) {}

    reveal(): void {
        this._popupFrame.classList.remove('is-hidden');
    }

    setPopupHeight(): void {
        this._sizeFrame();
    }

    expand(popupContainer: HTMLElement): void {
        const video = requireElement<HTMLElement>(document, '.js_videoTab video');
        const { height: videoHeight, top: videoTopOffset } = video.getBoundingClientRect();
        const portraitOrientation = window.innerWidth < window.innerHeight;

        // on mobile if the video is bellow the top of the viewport (not scrolled over)
        // scroll the page so that the top of the video aligns with the top of the viewport
        // and align the top of the alert with the bottom of the video
        // Double scroll because of potential Apple smart banner on iOS Safari w/ app installed
        if (portraitOrientation && !isDesktop()) {
            if (videoTopOffset > 0) {
                setTimeout(() => video.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' }), 100);
                video.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
            }
            popupContainer.style.height = `calc(100svh - ${videoHeight}px)`;
        }
    }

    close(popup: HTMLElement, popupContainer: HTMLElement): void {
        popupContainer.classList.add('is-closed');
        popup.removeAttribute('style');
    }

    reset(): void {
        resetShell(this._popupFrame);
    }

    destroy(): void {}

    private _sizeFrame(): void {
        sizeFrameToSidebar(this._popupFrame);
    }
}

/**
 * The iOS webview: layout is native, so the page only signals intent.
 */
export class IosHost implements Host {
    constructor(private readonly _popupFrame: HTMLElement) {}

    reveal(): void {
        this._popupFrame.classList.remove('is-hidden');
    }

    setPopupHeight(popup: HTMLElement, secondaryWrap: HTMLElement): void {
        popup.style.top = '24px';
        this._popupFrame.style.height = '100vh';

        // Scope to the active popup — during a transition two popups are in the DOM, so a document-wide
        // query could resolve the outgoing one.
        if (popup.classList.contains('brand-alert-popup--double-isi')) {
            secondaryWrap.style.top = '20px';
        }
    }

    expand(popupContainer: HTMLElement): void {
        // iOS: expand the popup to fist snap point
        popupContainer.style.height = '100vh';
        window.location.href = 'expand';

        setTimeout(() => {
            // disable transitions for smoother expanding on ios
            popupContainer.classList.add('transitions-disabled');
        }, 500);
    }

    close(): void {
        // iOS: should collapse the popup window
        window.location.href = 'close';
    }

    reset(): void {
        resetShell(this._popupFrame);
    }

    destroy(): void {}
}
